import { API_BASE } from '@/shared/services/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { toast } from 'sonner';
import { RefreshCw, Search, Settings } from 'lucide-react';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('ezerpToken')}`
});

export const PostingRulesPage = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/accounting/posting-rules`;
      if (eventTypeFilter !== 'all') url += `?event_type=${encodeURIComponent(eventTypeFilter)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch posting rules');
      const data = await res.json();
      setRules(Array.isArray(data) ? data : data.rules || []);
    } catch (err) {
      toast.error(err.message);
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [eventTypeFilter]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const eventTypes = useMemo(() => Array.from(new Set(rules.map((rule) => rule.event_type).filter(Boolean))).sort(), [rules]);
  const filteredRules = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rules;
    return rules.filter((rule) =>
      [rule.rule_name, rule.event_type, rule.rule_version].some((value) => String(value || '').toLowerCase().includes(needle))
    );
  }, [rules, search]);

  return (
    <div className="space-y-6" data-testid="posting-rules-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">Posting Rules</h1>
          <p className="text-muted-foreground mt-1">Review the accounting mappings that convert operational events into journal entries.</p>
        </div>
        <Button variant="outline" onClick={fetchRules} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-muted-foreground">Active Rules</p><p className="text-3xl font-bold">{rules.length}</p></div>
            <div className="p-3 rounded-lg bg-green-500/20"><Settings className="h-6 w-6 text-green-500" /></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <CardTitle>Rule Registry</CardTitle>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search rules..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 w-[240px]" />
              </div>
              <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                <SelectTrigger className="w-[220px]"><SelectValue placeholder="Event type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Event Types</SelectItem>
                  {eventTypes.map((eventType) => (
                    <SelectItem key={eventType} value={eventType}>{eventType}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="text-center py-12">
              <Settings className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">No posting rules found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Rule</th>
                    <th className="text-left p-3 font-medium">Event Type</th>
                    <th className="text-left p-3 font-medium">Version</th>
                    <th className="text-left p-3 font-medium">Effective</th>
                    <th className="text-left p-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {filteredRules.map((rule) => (
                    <tr key={rule.id} className="hover:bg-slate-800/30">
                      <td className="p-3 font-medium">{rule.rule_name || '-'}</td>
                      <td className="p-3"><Badge className="bg-blue-500/20 text-blue-400">{rule.event_type || '-'}</Badge></td>
                      <td className="p-3">{rule.rule_version || '-'}</td>
                      <td className="p-3 text-sm">{rule.effective_from || '-'}{rule.effective_to ? ` to ${rule.effective_to}` : ''}</td>
                      <td className="p-3"><Badge className="bg-green-500/20 text-green-400">active</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PostingRulesPage;
